import React from 'react';

import type * as api from '@gitbook/api-client';
import type { CommentContext } from '@gitbook/app-database';
import type { Document } from '@gitbook/slate';
import { Popover, PopoverRoot, PopoverTrigger, Skeleton } from '@gitbook/spine-react';

import { UserAvatarSkeleton } from '../../Users';
import { CommentInput } from '../CommentInput';
import { CommentTargetPreview } from '../CommentTargetPreview';
import { type CommentNodeTarget, type UseCommentsProps, useComments } from '../useComments';
import { PopoverContainer } from './PopoverContainer';

export type AddCommentPopoverProps = UseCommentsProps & {
    /** Popover placement */
    placement?: 'right-start' | 'bottom-start' | 'bottom' | 'top' | 'left' | 'right';
    /** Trigger element */
    trigger: React.ReactNode;
};

/**
 * Smart component that manages comment creation with popover state.
 */
export function AddCommentPopover(props: AddCommentPopoverProps) {
    const { nodeKey, tableCell, placement = 'right-start', trigger } = props;

    // Get the (pending) comment state and handlers
    const { commentContext, nodeState, submitComment, closePopover } = useComments({
        nodeKey,
        tableCell,
    });

    return (
        <PopoverRoot
            isOpen={nodeState.addNewPopoverIsOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    closePopover();
                }
            }}
        >
            <PopoverTrigger>{trigger}</PopoverTrigger>
            <Popover
                placement={placement}
                sticky={{
                    boundary: document.querySelector('[data-comments-boundary]') ?? undefined,
                }}
            >
                <React.Suspense fallback={<AddCommentPopoverSkeleton />}>
                    {nodeState.addNewPopoverIsOpen ? (
                        <AddCommentPopoverContent
                            nodeKey={nodeKey}
                            tableCell={tableCell}
                            commentContext={commentContext}
                            submitComment={submitComment}
                            closePopover={closePopover}
                        />
                    ) : null}
                </React.Suspense>
            </Popover>
        </PopoverRoot>
    );
}

function AddCommentPopoverContent(props: {
    commentContext: CommentContext;
    nodeKey: CommentNodeTarget;
    tableCell?: api.TableCellTarget;
    submitComment: (body: Document) => Promise<void>;
    closePopover: () => void;
}) {
    const { commentContext, nodeKey, tableCell, submitComment, closePopover } = props;

    return (
        <PopoverContainer>
            <div className="p-2">
                {commentContext.page ? (
                    <CommentTargetPreview
                        pageId={commentContext.page}
                        spaceId={commentContext.space}
                        tableCell={tableCell}
                        nodeKey={nodeKey}
                    />
                ) : null}
            </div>
            <CommentInput
                testID="comment.postFirstNodeComment"
                placeholder="Leave a comment..."
                targetNode={nodeKey}
                onSubmit={submitComment}
                onCancel={closePopover}
                withAgentMentions={!!commentContext.changeRequest}
            />
        </PopoverContainer>
    );
}

function AddCommentPopoverSkeleton() {
    return (
        <div className="flex h-fit min-w-76 max-w-76 flex-col gap-2 overflow-hidden rounded-lg border border-base bg-base p-2 shadow-subtle">
            {/* Preview area */}
            <div className="flex flex-row gap-2">
                <Skeleton className="h-5 w-0.5 rounded-full" />
                <Skeleton className="h-5 w-3/4" />
            </div>

            {/* Comment input area */}
            <div className="flex items-center gap-2">
                <UserAvatarSkeleton size="sm" />
                <Skeleton className="h-9 flex-1 rounded-lg" />
            </div>
            {/* Comment actions */}
            <div className="ml-8 flex flex-1 items-center justify-between gap-2">
                <div className="flex gap-1">
                    <Skeleton className="h-6 w-6" />
                    <Skeleton className="h-6 w-6" />
                    <Skeleton className="h-6 w-6" />
                    <Skeleton className="h-6 w-6" />
                </div>
                <Skeleton className="h-6 w-16" />
            </div>
        </div>
    );
}
